// ===================================================
// Action Permissions — per-user, per-scope
// ===================================================
// A permission row is (user, scope), where scope is global | app | entity_type.
// The specific beats the general: entity_type > app > global.
//
// The merge rule is NOT reimplemented here. It lives in the SQL function
// `resolve_action_permissions`, which the API layer and any RLS policy call
// too — one definition, so the three enforcement layers cannot drift.
//
// An absent row denies. Until 2026-08-20 it fell back to `member`, which
// handed edit + CSV export to every authenticated user by default.
// Spec: docs/specs/per-app-permission-layer.md

import { supabase } from '@/lib/supabaseClient';
import type { UserRole } from '@/contexts/AuthContext';

export interface ActionPermissions {
  canView: boolean;
  canExportCsv: boolean;
  canDelete: boolean;
  canBulkUpdate: boolean;
  canBulkStatus: boolean;
  canBulkAssign: boolean;
  canManageUsers: boolean;
  canEdit: boolean;
}

/** Which slice of the system the question is about. Omit for the global layer. */
export interface PermissionScope {
  appId?: string | null;
  entityType?: string | null;
}

/** The answer when nothing grants anything — including while permissions load. */
export const DENIED_ACTIONS: ActionPermissions = {
  canView: false,
  canExportCsv: false,
  canDelete: false,
  canBulkUpdate: false,
  canBulkStatus: false,
  canBulkAssign: false,
  canManageUsers: false,
  canEdit: false,
};

const ALL_ACTIONS: ActionPermissions = {
  canView: true,
  canExportCsv: true,
  canDelete: true,
  canBulkUpdate: true,
  canBulkStatus: true,
  canBulkAssign: true,
  canManageUsers: true,
  canEdit: true,
};

/** Map from action button ID → ActionPermissions key */
export const ACTION_PERMISSION_MAP: Record<string, keyof ActionPermissions> = {
  export_csv: 'canExportCsv',
  deactivate: 'canDelete',
  bulk_field_update: 'canBulkUpdate',
  bulk_status_change: 'canBulkStatus',
  bulk_assign: 'canBulkAssign',
  // Single-action permissions
  change_status: 'canEdit',
  open_in_ai: 'canEdit',
  send_notification: 'canEdit',
  call_log: 'canEdit',
  send_whatsapp: 'canEdit',
  reactivate: 'canEdit',
};

/**
 * Role-based defaults.
 *
 * Kept for the admin ceiling and for callers that need to describe a role,
 * but it is NO LONGER the fallback for a missing permission row — that is
 * `DENIED_ACTIONS`. The `member` branch below is exactly what made every
 * authenticated user an editor with export.
 */
export function roleDefaults(role: UserRole): ActionPermissions {
  switch (role) {
    case 'admin':
      return { ...ALL_ACTIONS };
    case 'manager':
      return { ...ALL_ACTIONS, canManageUsers: false };
    case 'member':
      return {
        canView: true, canExportCsv: true, canDelete: false, canBulkUpdate: true,
        canBulkStatus: true, canBulkAssign: false, canManageUsers: false, canEdit: true,
      };
    case 'viewer':
    case 'external':
      return { ...DENIED_ACTIONS, canView: true };
    default:
      return { ...DENIED_ACTIONS };
  }
}

interface ResolvedRow {
  can_view: boolean | null;
  can_edit: boolean | null;
  can_delete: boolean | null;
  can_export_csv: boolean | null;
  can_bulk_update: boolean | null;
  can_bulk_status: boolean | null;
  can_bulk_assign: boolean | null;
  can_manage_users: boolean | null;
  resolved_from: string | null;
}

/**
 * What may this user do inside this scope?
 *
 * Anything other than an explicit `true` from the resolver is a denial —
 * including a network error, an empty result, or an unknown column.
 */
export async function fetchActionPermissions(
  userId: string,
  role: UserRole,
  scope: PermissionScope = {},
): Promise<ActionPermissions> {
  if (role === 'admin') return { ...ALL_ACTIONS };

  const { data, error } = await supabase.rpc('resolve_action_permissions', {
    p_user: userId,
    p_app_id: scope.appId ?? null,
    p_entity_type: scope.entityType ?? null,
  });

  if (error) {
    console.error('fetchActionPermissions:', error.message);
    return { ...DENIED_ACTIONS };
  }

  const row = (Array.isArray(data) ? data[0] : data) as ResolvedRow | undefined;
  if (!row) return { ...DENIED_ACTIONS };

  return {
    canView: row.can_view === true,
    canEdit: row.can_edit === true,
    canDelete: row.can_delete === true,
    canExportCsv: row.can_export_csv === true,
    canBulkUpdate: row.can_bulk_update === true,
    canBulkStatus: row.can_bulk_status === true,
    canBulkAssign: row.can_bulk_assign === true,
    canManageUsers: row.can_manage_users === true,
  };
}

/**
 * Admin: write one user's grant for one scope.
 *
 * Goes through the admin API route on purpose. `user_action_permissions` has
 * no INSERT/UPDATE policy for `authenticated` — a user must not be able to
 * widen their own grant from the browser — so the write needs the service
 * role, and the route checks `can_manage_users` before it uses it.
 */
export async function updateActionPermissions(
  userId: string,
  scope: PermissionScope & { scopeKind: 'global' | 'app' | 'entity_type' },
  patch: Partial<Record<string, boolean | null>>,
): Promise<boolean> {
  const res = await fetch('/api/admin/permissions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      user_id: userId,
      scope_kind: scope.scopeKind,
      scope_id: scope.scopeKind === 'global'
        ? null
        : (scope.appId ?? scope.entityType ?? null),
      patch,
    }),
  });
  if (!res.ok) {
    console.error('updateActionPermissions:', await res.text());
    return false;
  }
  return true;
}
